import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { Button, Modal } from '@/components';
import {
  scheduledInstantAPI,
  type CreateSlotRiderInput,
} from '@/services/api/scheduled-instant';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';

import { RiderPicker, type RiderSelection } from './pickers';

const inputCls =
  'w-full rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-ink outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:bg-gray-100 disabled:text-gray-400';
const lockedCls =
  'w-full rounded-gsm border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 flex items-center justify-between';

/**
 * Edit Slot — a single dated slot (Screen 9). Client is locked; date, time
 * window, and the rider set are editable for THIS slot only (master jadwal
 * untouched). Reason required. Can also delete just this slot. Wraps
 * PATCH/DELETE /slots/:id (roster reconcile + audit; past slots rejected).
 */
const EditSlotModal: React.FC<{
  slotId: string | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ slotId, onClose, onSaved }) => {
  const open = !!slotId;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState('');
  const [date, setDate] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [fromMasterJadwal, setFromMasterJadwal] = useState(false);
  const [utama, setUtama] = useState<RiderSelection[]>([]);
  const [cadangan, setCadangan] = useState<RiderSelection[]>([]);
  const [reason, setReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    if (!slotId) return;
    let cancelled = false;
    setLoading(true);
    setReason('');
    setConfirmDelete(false);
    scheduledInstantAPI
      .getSlot(slotId)
      .then((d) => {
        if (cancelled) return;
        setClient(d.slot.clientName);
        setDate(d.slot.scheduleDate);
        setStart(d.slot.windowStartTime);
        setEnd(d.slot.windowEndTime);
        setFromMasterJadwal(!!d.slot.scheduleId);
        const toSel = (r: {
          riderCode: string;
          riderName: string;
          riderPhone: string;
          isDayOff: boolean;
        }) => ({
          riderCode: r.riderCode,
          riderName: r.riderName,
          riderPhone: r.riderPhone,
          isDayOff: r.isDayOff,
        });
        setUtama(d.mainRiders.map(toSel));
        setCadangan(d.reserveRiders.map(toSel));
      })
      .catch((e) =>
        showErrorToast('Gagal memuat slot', (e as Error).message)
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slotId]);

  const canSave =
    !loading && !saving && utama.length > 0 && reason.trim().length > 0;

  const save = async () => {
    if (!slotId || !canSave) return;
    setSaving(true);
    try {
      const riders: CreateSlotRiderInput[] = [
        ...utama.map((r) => ({ ...r, role: 'utama' as const })),
        ...cadangan.map((r) => ({ ...r, role: 'cadangan' as const })),
      ];
      await scheduledInstantAPI.updateSlot(slotId, {
        scheduleDate: date,
        windowStartTime: start,
        windowEndTime: end,
        riders,
        reason: reason.trim(),
      });
      showSuccessToast('Slot diperbarui', `${client} · ${date}`);
      onSaved();
      onClose();
    } catch (e) {
      showErrorToast('Gagal menyimpan', (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!slotId) return;
    setDeleting(true);
    try {
      await scheduledInstantAPI.deleteSlot(slotId);
      showSuccessToast('Slot dihapus', `${client} · ${date}`);
      onSaved();
      onClose();
    } catch (e) {
      showErrorToast('Gagal menghapus slot', (e as Error).message);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-calendar-event-line"
      iconTone="primary"
      title="Edit Slot"
      subtitle={client && date ? `${client} · ${dayjs(date).format('DD MMM YYYY')}` : ''}
      width="lg"
      confirmOnClose
      closeOnBackdropClick={!saving}
      footer={
        confirmDelete ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] text-red-800 font-medium">
              Hapus slot ini? Hanya slot tanggal ini yang dihapus, master jadwal
              tidak berubah.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="white"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Batal
              </Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Menghapus…' : 'Ya, Hapus'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
            >
              <i className="ri-delete-bin-line mr-1 text-red-500" />
              <span className="text-red-500">Hapus Slot</span>
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="white" onClick={onClose} disabled={saving}>
                Batal
              </Button>
              <Button onClick={save} disabled={!canSave}>
                {saving ? 'Menyimpan…' : 'Simpan Perubahan'}
              </Button>
            </div>
          </div>
        )
      }
    >
      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Memuat…</p>
      ) : (
        <div className="space-y-4">
          {fromMasterJadwal ? (
            <div className="rounded-gsm bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800">
              Dari master jadwal — perubahan ini hanya berlaku untuk tanggal
              ini. Untuk mengubah jadwal berulang, gunakan Edit Jadwal di
              Detail Jadwal.
            </div>
          ) : (
            <div className="rounded-gsm bg-sky-50 border border-sky-200 px-3 py-2 text-[11px] text-sky-800">
              Slot bebas, tidak terikat master jadwal. Perubahan berlaku
              langsung untuk slot ini.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                Klien
              </label>
              <div className={lockedCls}>
                {client} <i className="ri-lock-line text-gray-400" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                Tanggal
              </label>
              <input
                type="date"
                className={inputCls}
                value={date}
                min={today}
                onChange={(e) => setDate(e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                Jam Mulai
              </label>
              <input
                type="time"
                className={inputCls}
                value={start}
                onChange={(e) => setStart(e.target.value)}
                disabled={saving}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                Jam Selesai
              </label>
              <input
                type="time"
                className={inputCls}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <RiderPicker
            open={open}
            title="Rider Utama"
            required
            riders={utama}
            onChange={setUtama}
            disabled={saving}
          />
          <RiderPicker
            open={open}
            title="Rider Cadangan"
            hint="opsional"
            riders={cadangan}
            onChange={setCadangan}
            disabled={saving}
          />

          <div>
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
              Alasan Perubahan <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={2}
              className={inputCls}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="cth. klien minta pickup diundur khusus hari ini"
              disabled={saving}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditSlotModal;
